import { useQueryClient } from "@tanstack/react-query";
import { USDTAbi } from "modules/bridge/hooks/useERC20Approve/USDTAbi";
import { scopeKeyPredicate } from "modules/shared/api/scopeKeyPredicate";
import { useEffect } from "react";
import { type Address, erc20Abi } from "viem";
import { useWaitForTransactionReceipt, useWriteContract } from "wagmi";

type ApproveArgs = {
  tokenAddress: Address;
  spender: Address;
  amount: bigint;
};

export const useERC20Approve = () => {
  const queryClient = useQueryClient();
  const { writeContract, writeContractAsync, data, ...restWrite } =
    useWriteContract();
  const { data: receipt, ...restWait } = useWaitForTransactionReceipt({
    hash: data,
  });

  useEffect(() => {
    if (receipt) {
      queryClient.invalidateQueries({
        predicate: scopeKeyPredicate(["allowance"]),
      });
    }
  }, [receipt, queryClient]);

  const approve =
    <T extends typeof writeContract | typeof writeContractAsync>(
      approveFn: T
    ) =>
    ({ tokenAddress, spender, amount }: ApproveArgs): ReturnType<T> => {
      return approveFn({
        abi:
          tokenAddress.toLowerCase() ===
          "0xdac17f958d2ee523a2206206994597c13d831ec7"
            ? USDTAbi
            : erc20Abi,
        address: tokenAddress,
        functionName: "approve",
        args: [spender, amount],
      }) as ReturnType<T>;
    };

  return {
    receipt,
    approve: approve(writeContract),
    approveAsync: approve(writeContractAsync),
    state: restWrite,
    receiptState: restWait,
  };
};
